import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Send, Plus, AlertCircle, CheckCircle2, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { legalEntitiesAPI, saleInvoicesAPI } from '../../services/api';
import { formatAmount } from '../../utils/currency';
import GenerateFromTimesheetsModal from './GenerateFromTimesheetsModal';

const STATUT_CLASS = {
  NEW: 'pd-chip',
  EN_ATTENTE: 'pd-chip',
  VALIDEE: 'pd-chip pd-chip-ok',
  PAYEE: 'pd-chip pd-chip-ok',
  ANNULEE: 'pd-chip pd-chip-danger',
};

const EINVOICE_LABEL = {
  DRAFT: 'Brouillon',
  GENERATED: 'Généré',
  SIGNED: 'Signé',
  TRANSMITTED: 'Transmis',
  ACCEPTED: 'Accepté',
  REJECTED: 'Rejeté',
};

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function ProjectSalesInvoices({ project, canEdit }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saleInvoicesAPI.list({ projet_id: project.id, limit: 200 });
      setInvoices(data.items || []);
    } catch {
      toast.error('Impossible de charger les factures de ventes');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    legalEntitiesAPI.list(true).then(({ data }) => setEntities(data)).catch(() => {});
  }, []);

  const handleIssue = async (inv) => {
    setBusyId(inv.id);
    try {
      await saleInvoicesAPI.issue(inv.id);
      toast.success('Facture émise');
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.issues) {
        toast.error(`${detail.issues.length} mention(s) obligatoire(s) manquante(s)`);
        detail.issues.slice(0, 4).forEach((i) => toast.error(i.message, { duration: 6000 }));
      } else {
        toast.error(detail || 'Erreur lors de l’émission');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (inv, kind) => {
    setBusyId(inv.id);
    try {
      const res = kind === 'pdf'
        ? await saleInvoicesAPI.downloadPdf(inv.id)
        : await saleInvoicesAPI.downloadXml(inv.id);
      downloadBlob(res.data, `${inv.ref}.${kind}`);
    } catch (err) {
      toast.error(err?.response?.status === 503
        ? 'Le moteur de rendu PDF n’est pas disponible sur le serveur'
        : 'Téléchargement impossible');
    } finally {
      setBusyId(null);
    }
  };

  const handleTransmit = async (inv) => {
    setBusyId(inv.id);
    try {
      const { data } = await saleInvoicesAPI.transmit(inv.id);
      toast.success(`Transmission : ${EINVOICE_LABEL[data.einvoice_status] || data.einvoice_status}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transmission impossible');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)' }}>Chargement…</div>;
  }

  return (
    <>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowGenerate(true)}>
            <Plus size={13} /> Générer depuis les feuilles de temps
          </button>
          <button className="btn btn-sm" onClick={() => navigate(`/sale-invoices/new?projet_id=${project.id}`)}>
            <FileText size={13} /> Facture manuelle
          </button>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-faint)' }}>
          <AlertCircle size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Aucune facture de vente</div>
          <div style={{ fontSize: '0.8125rem', marginTop: 6 }}>
            Générez une facture depuis les feuilles de temps validées du mois.
          </div>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="pd-table">
            <thead>
              <tr>
                <th>Référence</th>
                <th>Entité</th>
                <th>Date</th>
                <th>Échéance</th>
                <th style={{ textAlign: 'right' }}>Total HT</th>
                <th style={{ textAlign: 'right' }}>TVA</th>
                <th style={{ textAlign: 'right' }}>Total TTC</th>
                <th>Statut</th>
                <th>Facture élec.</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <button className="pd-link" onClick={() => navigate(`/sale-invoices/${inv.id}`)}>
                      {inv.ref}
                    </button>
                  </td>
                  <td>{inv.legal_entity_nom || '—'}<div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{inv.country_code || ''}</div></td>
                  <td>{inv.date_facture || '—'}</td>
                  <td>{inv.date_echeance || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(inv.total_ht, inv.currency)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(inv.total_tva, inv.currency)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(inv.total_ttc, inv.currency)}</td>
                  <td><span className={STATUT_CLASS[inv.statut] || 'pd-chip'}>{inv.statut}</span></td>
                  <td>
                    <span className="pd-chip">{EINVOICE_LABEL[inv.einvoice_status] || inv.einvoice_status}</span>
                    {inv.einvoice_format && <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{inv.einvoice_format}</div>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {busyId === inv.id ? (
                      <Loader size={14} className="spin" />
                    ) : (
                      <>
                        {inv.is_draft && canEdit && (
                          <button className="pd-icon-btn" title="Émettre la facture" onClick={() => handleIssue(inv)}>
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        <button className="pd-icon-btn" title="Télécharger le PDF" onClick={() => handleDownload(inv, 'pdf')}>
                          <Download size={14} />
                        </button>
                        {inv.einvoice_format && inv.einvoice_format !== 'PDF_MENTIONS' && (
                          <button className="pd-icon-btn" title="Télécharger le XML" onClick={() => handleDownload(inv, 'xml')}>
                            <FileText size={14} />
                          </button>
                        )}
                        {!inv.is_draft && canEdit && (
                          <button className="pd-icon-btn" title="Transmettre" onClick={() => handleTransmit(inv)}>
                            <Send size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGenerate && (
        <GenerateFromTimesheetsModal
          project={project}
          entities={entities}
          onClose={() => setShowGenerate(false)}
          onCreated={() => { setShowGenerate(false); load(); }}
        />
      )}
    </>
  );
}
